// BrowseCourses.tsx
import React, { useEffect } from 'react';
import './BrowseCourses.css';
import CourseCard from '@/Components/CourseCard/CourseCard';
import { useInstructorLearningsStore } from '@/stores/InstructorStore/InstructorLearningsStore';
import { LoadingSpinner, ErrorDisplay } from '@/Components/common';

const BrowseCourses: React.FC = () => {
  const {
    allActiveCourses,
    loadingAllActive,
    errorAllActive,
    savedCourseIds,
    fetchAllActiveCourses,
    fetchSavedCourseIds,
    toggleSaveCourse,
  } = useInstructorLearningsStore();

  // Fetch data on mount
  useEffect(() => {
    fetchAllActiveCourses();
    fetchSavedCourseIds(); // Fetch saved status on mount
  }, [fetchAllActiveCourses, fetchSavedCourseIds]);

  // Handle save/unsave course
  const handleToggleSave = async (courseId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation to course detail

    try {
      await toggleSaveCourse(courseId);
      // Success feedback can be added here (toast, snackbar, etc.)
    } catch (error: any) {
      console.error('Failed to toggle save:', error);
      // Error feedback can be added here (toast, snackbar, etc.)
    }
  };

  // Loading state
  if (loadingAllActive) {
    return (
      <LoadingSpinner
        fullScreen
        size="lg"
      />
    );
  }

  // Error state
  if (errorAllActive) {
    return (
      <ErrorDisplay
        fullScreen
        title="Failed to Load Browse Courses"
        message={errorAllActive}
        type="error"
        showRetry
        onRetry={fetchAllActiveCourses}
      />
    );
  }

  // Show all active courses (no filtering by enrollment status)
  const availableCourses = allActiveCourses || [];

  // Empty state
  if (availableCourses.length === 0) {
    return (
      <div className="browse-courses-container">
        <div className="browse-courses-header">
          <h1 className='page-title'>Browse Courses</h1>
          <p className='page-subtitle'>Explore our wide range of courses and start learning today</p>
        </div>
        <ErrorDisplay
          type="info"
          title="No Courses Available"
          message="There are no courses available to browse at the moment. Check back later for new courses!"
          showRetry={false}
        />
      </div>
    );
  }

  return (
    <div className="browse-courses-container">
      <div className="browse-courses-header">
        <h1 className='page-title'>Browse Courses</h1>
        <p className='page-subtitle'>Explore our wide range of courses and start learning today</p>
      </div>
      <div className="browse-courses-grid">
        {availableCourses.map((course) => (
          <CourseCard
            key={course.id}
            course={course}
            isSaved={savedCourseIds.has(course.id)}
            onToggleSave={(e) => handleToggleSave(course.id, e)}
          />
        ))}
      </div>
    </div>
  );
};

export default BrowseCourses;
